import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Stage, Layer, Rect, Transformer, Group, Image as KonvaImage, Shape } from 'react-konva';
import { observer } from 'mobx-react-lite';
import ElementRenderer from './element';
import InputFieldsDialog from '../components/InputFieldsDialog';

// Generate a checkerboard pattern tile (off-screen canvas)
const createCheckerboardImage = (tileSize = 10, color1 = '#d4d4d4', color2 = '#ffffff') => {
  const canvas = document.createElement('canvas');
  canvas.width = tileSize * 2;
  canvas.height = tileSize * 2;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = color1;
  ctx.fillRect(0, 0, tileSize, tileSize);
  ctx.fillRect(tileSize, tileSize, tileSize, tileSize);
  ctx.fillStyle = color2;
  ctx.fillRect(tileSize, 0, tileSize, tileSize);
  ctx.fillRect(0, tileSize, tileSize, tileSize);
  const img = new window.Image();
  img.src = canvas.toDataURL();
  return img;
};

let _checkerImg = null;
const getCheckerboardImage = () => {
  if (!_checkerImg) _checkerImg = createCheckerboardImage();
  return _checkerImg;
};

const CheckerboardBackground = ({ w, h }) => {
  const [ready, setReady] = useState(false);
  const imgRef = useRef(getCheckerboardImage());
  useEffect(() => {
    const img = imgRef.current;
    if (img.complete) { setReady(true); return; }
    img.onload = () => setReady(true);
    return () => { img.onload = null; };
  }, []);
  if (!ready) return <Rect x={0} y={0} width={w} height={h} fill="#ffffff" listening={false} name="display-only" />;
  return (
    <Rect
      x={0} y={0} width={w} height={h}
      fillPatternImage={imgRef.current}
      fillPatternRepeat="repeat"
      listening={false}
      name="display-only"
    />
  );
};

const ImageBackground = ({ url, w, h }) => {
  const [image, setImage] = useState(null);
  useEffect(() => {
    if (!url) { setImage(null); return; }
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setImage(img);
    img.onerror = () => setImage(null);
    img.src = url;
    return () => { img.onload = null; img.onerror = null; };
  }, [url]);
  if (!image) return <Rect x={0} y={0} width={w} height={h} fill="#f0f0f0" listening={false} name="page-background" />;

  // "cover" fit: scale to fill canvas while maintaining aspect ratio, then center
  const imgW = image.naturalWidth || image.width;
  const imgH = image.naturalHeight || image.height;
  const scale = Math.max(w / imgW, h / imgH);
  const drawW = imgW * scale;
  const drawH = imgH * scale;
  const drawX = (w - drawW) / 2;
  const drawY = (h - drawH) / 2;

  return (
    <Group clipX={0} clipY={0} clipWidth={w} clipHeight={h} listening={false} name="page-background">
      <KonvaImage x={drawX} y={drawY} width={drawW} height={drawH} image={image} listening={false} />
    </Group>
  );
};

const PageBackground = observer(({ store }) => {
  const page = store.activePage;
  if (!page) return null;

  const bg = page.background;
  const w = store.width;
  const h = store.height;

  if (!bg) {
    return <Rect x={0} y={0} width={w} height={h} fill="#ffffff" listening={false} name="page-background" />;
  }

  if (bg === 'transparent') {
    return (
      <>
        <Rect x={0} y={0} width={w} height={h} fill="#ffffff" listening={false} name="page-background" />
        <CheckerboardBackground w={w} h={h} />
      </>
    );
  }

  // Linear gradient stored as JSON
  if (bg.startsWith('{')) {
    try {
      const grad = JSON.parse(bg);
      if (grad.type === 'linear') {
        const angle = (grad.angle || 0) * (Math.PI / 180);
        const cx = w / 2, cy = h / 2;
        const len = Math.sqrt(w * w + h * h) / 2;
        const x1 = cx - len * Math.cos(angle);
        const y1 = cy - len * Math.sin(angle);
        const x2 = cx + len * Math.cos(angle);
        const y2 = cy + len * Math.sin(angle);
        return (
          <Rect
            x={0} y={0} width={w} height={h}
            fillLinearGradientStartPoint={{ x: x1, y: y1 }}
            fillLinearGradientEndPoint={{ x: x2, y: y2 }}
            fillLinearGradientColorStops={[0, grad.colors?.[0] || '#000000', 1, grad.colors?.[1] || '#ffffff']}
            listening={false}
            name="page-background"
          />
        );
      }
    } catch (e) { /* fall through */ }
  }

  // Image URL background
  if (bg.startsWith('http')) {
    return <ImageBackground url={bg} w={w} h={h} />;
  }

  // Solid color
  if (bg.startsWith('#') || bg.startsWith('rgb') || bg.startsWith('hsl') || ['white', 'black', 'red', 'blue', 'green'].includes(bg)) {
    return <Rect x={0} y={0} width={w} height={h} fill={bg} listening={false} name="page-background" />;
  }

  return <Rect x={0} y={0} width={w} height={h} fill="#ffffff" listening={false} name="page-background" />;
});

// Semi-transparent overlay that dims everything outside the page boundary.
// Draws a huge rectangle with a rectangular hole cut out for the page area.
const PageOverlayFrame = observer(({ store }) => {
  const W = store.width;
  const H = store.height;
  if (!W || !H) return null;
  const BIG = 100000; // large enough to cover viewport at any zoom
  return (
    <Shape
      sceneFunc={(ctx, shape) => {
        ctx.beginPath();
        // Outer rectangle (clockwise)
        ctx.moveTo(-BIG, -BIG);
        ctx.lineTo(BIG, -BIG);
        ctx.lineTo(BIG, BIG);
        ctx.lineTo(-BIG, BIG);
        ctx.closePath();
        // Inner rectangle (counter-clockwise to create hole)
        ctx.moveTo(0, 0);
        ctx.lineTo(0, H);
        ctx.lineTo(W, H);
        ctx.lineTo(W, 0);
        ctx.closePath();
        ctx.fillStrokeShape(shape);
      }}
      fill="rgba(240,240,240,0.5)"
      listening={false}
      name="display-only"
    />
  );
});

const ALL_ANCHORS = [
  'top-left',
  'top-center',
  'top-right',
  'middle-left',
  'middle-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];

const TEXT_ANCHORS = [
  'top-left',
  'top-right',
  'middle-left',
  'middle-right',
  'bottom-left',
  'bottom-right',
];

const CORNER_ANCHORS = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
];

const SelectionTransformer = observer(({ store, stageRef }) => {
  const transformerRef = useRef(null);

  const selectedIds = [...store.selectedElementsIds];

  // Filter out line elements — they use custom endpoint handles instead
  const nonLineIds = selectedIds.filter((id) => {
    const el = store.getElementById(id);
    return el && el.type !== 'line';
  });

  const allText = nonLineIds.length === 1 && (() => {
    const el = store.getElementById(nonLineIds[0]);
    return el && el.type === 'text';
  })();

  const useCorners = nonLineIds.length > 1 || (nonLineIds.length === 1 && (() => {
    const el = store.getElementById(nonLineIds[0]);
    return el && el.type === 'group';
  })());

  const targetAnchors = useCorners ? CORNER_ANCHORS : allText ? TEXT_ANCHORS : ALL_ANCHORS;

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (!transformerRef.current || !stageRef.current) return;

      const stage = stageRef.current;
      const nodes = [];

      nonLineIds.forEach((id) => {
        const node = stage.findOne(`#${id}`);
        if (node) nodes.push(node);
      });

      transformerRef.current.enabledAnchors(targetAnchors);
      transformerRef.current.nodes(nodes);
      transformerRef.current.getLayer()?.batchDraw();
    });
    return () => cancelAnimationFrame(frame);
  }, [nonLineIds.length, nonLineIds.join(','), targetAnchors]);

  return (
    <Transformer
      ref={transformerRef}
      rotateEnabled={true}
      enabledAnchors={ALL_ANCHORS}
      boundBoxFunc={(oldBox, newBox) => {
        // Reject flip (negative) and too-small dimensions
        if (newBox.width < 5 || newBox.height < 5) {
          return oldBox;
        }
        return newBox;
      }}
      anchorSize={8}
      anchorCornerRadius={2}
      borderStroke="#0d83cd"
      anchorStroke="#0d83cd"
      anchorFill="#ffffff"
      padding={0}
    />
  );
});

const Workspace = observer(({ store, components = {}, showInputFieldsPopup = true, onInputFieldsComplete, backTrigger = 0 }) => {
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });

  const { PageControls } = components;

  // Resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateSize = () => {
      const { width, height } = container.getBoundingClientRect();
      setStageSize({ width, height });

      // Auto-fit the canvas.
      // Guard: if the container hasn't been laid out yet (width/height === 0)
      // the arithmetic produces a negative scale which mirrors the entire canvas.
      // Skip the update in that case — the ResizeObserver will fire again once
      // the container has real dimensions.
      if (store.width > 0 && store.height > 0 && width > 0 && height > 0) {
        const padding = 40;
        const scaleX = (width - padding * 2) / store.width;
        const scaleY = (height - padding * 2) / store.height;
        const scale = Math.min(scaleX, scaleY, 1);
        if (scale > 0) {
          store._setScaleToFit(scale);
          if (store.scale === 1) {
            store.setScale(scale);
          }
        }
      }
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(container);

    return () => observer.disconnect();
  }, [store.width, store.height]);

  // Set stage ref on store for export
  useEffect(() => {
    if (stageRef.current) {
      store._setStageRef(stageRef.current);
    }
  }, [stageRef.current]);

  const handleStageMouseDown = useCallback(
    (e) => {
      let target = e.target;

      // Click on empty area or background -> deselect
      if (target === target.getStage() || target.attrs?.name === 'page-background') {
        store.selectElements([]);
        return;
      }

      // Walk up the node tree to find the nearest ancestor with an element id
      // (needed for Group-wrapped elements like lines)
      let clickedId = target.id();
      while (!clickedId && target.parent && target.parent !== target.getStage()) {
        target = target.parent;
        clickedId = target.id();
      }
      if (!clickedId) return;

      const element = store.getElementById(clickedId);
      if (!element || !element.selectable) return;

      // If element is inside a group, select the top-level group instead
      const targetElement = element.top || element;
      const targetId = targetElement.id;

      if (e.evt?.ctrlKey || e.evt?.metaKey) {
        // Multi-select with Ctrl (Cmd on Mac)
        const currentIds = [...store.selectedElementsIds];
        const idx = currentIds.indexOf(targetId);
        if (idx >= 0) {
          currentIds.splice(idx, 1);
        } else {
          currentIds.push(targetId);
        }
        store.selectElements(currentIds);
      } else if (!store.selectedElementsIds.includes(targetId)) {
        store.selectElements([targetId]);
      }
    },
    [store]
  );

  const handleTransformEnd = useCallback(() => {
    // Save to history after transform
    store.history.addUndoState();
  }, [store]);

  const handleDragEnd = useCallback(() => {
    // Save to history after drag
    store.history.addUndoState();
  }, [store]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle if stage container or body is focused
      const target = e.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }

      // Delete / Backspace
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const selectedIds = [...store.selectedElementsIds];
        if (selectedIds.length > 0) {
          store.deleteElements(selectedIds);
          store.history.addUndoState();
          e.preventDefault();
        }
      }

      // Ctrl+Z / Cmd+Z -> Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        store.history.undo();
        e.preventDefault();
      }

      // Ctrl+Y / Cmd+Shift+Z -> Redo
      if (
        ((e.ctrlKey || e.metaKey) && e.key === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z')
      ) {
        store.history.redo();
        e.preventDefault();
      }

      // Ctrl+D -> Duplicate
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        const selectedIds = [...store.selectedElementsIds];
        if (selectedIds.length > 0 && store.activePage) {
          const newIds = [];
          selectedIds.forEach((id) => {
            const el = store.getElementById(id);
            if (el && el.clone) {
              const cloned = el.clone({ x: (el.x || 0) + 20, y: (el.y || 0) + 20 }, { skipSelect: true });
              if (cloned) newIds.push(cloned.id);
            }
          });
          store.selectElements(newIds);
          store.history.addUndoState();
          e.preventDefault();
        }
      }

      // Ctrl+G -> Group selected elements
      if ((e.ctrlKey || e.metaKey) && e.key === 'g' && !e.shiftKey) {
        const selectedIds = [...store.selectedElementsIds];
        if (selectedIds.length > 1) {
          const group = store.groupElements(selectedIds);
          if (group) {
            store.selectElements([group.id]);
            store.history.addUndoState();
          }
          e.preventDefault();
        }
      }

      // Ctrl+Shift+G -> Ungroup
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'G') {
        const selectedIds = [...store.selectedElementsIds];
        if (selectedIds.length === 1) {
          const el = store.getElementById(selectedIds[0]);
          if (el && el.type === 'group') {
            store.ungroupElements([el.id]);
            store.history.addUndoState();
            e.preventDefault();
          }
        }
      }

      // Arrow keys -> nudge
      const NUDGE = e.shiftKey ? 10 : 1;
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        const selectedIds = [...store.selectedElementsIds];
        if (selectedIds.length > 0) {
          selectedIds.forEach((id) => {
            const el = store.getElementById(id);
            if (el && el.set) {
              if (e.key === 'ArrowLeft') el.set({ x: el.x - NUDGE });
              if (e.key === 'ArrowRight') el.set({ x: el.x + NUDGE });
              if (e.key === 'ArrowUp') el.set({ y: el.y - NUDGE });
              if (e.key === 'ArrowDown') el.set({ y: el.y + NUDGE });
            }
          });
          e.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [store]);

  // Zoom with mouse wheel
  const handleWheel = useCallback(
    (e) => {
      e.evt.preventDefault();
      const stage = stageRef.current;
      if (!stage) return;

      const scaleBy = 1.05;
      const oldScale = store.scale;
      const newScale =
        e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;

      store.setScale(Math.max(0.1, Math.min(5, newScale)));
    },
    [store]
  );

  const page = store.activePage;
  const scale = store.scale;
  // Reading _fontVersion makes this observer re-render whenever a font finishes
  // loading, so Konva redraws text with the correct font instead of the fallback.
  void store._fontVersion;

  // Center the canvas in the viewport
  const offsetX = (stageSize.width - store.width * scale) / 2;
  const offsetY = (stageSize.height - store.height * scale) / 2;

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
        background: '#f0f0f0',
      }}
    >
      <Stage
        ref={stageRef}
        width={stageSize.width}
        height={stageSize.height}
        onMouseDown={handleStageMouseDown}
        onTouchStart={handleStageMouseDown}
        onWheel={handleWheel}
      >
        <Layer
          x={offsetX}
          y={offsetY}
          scaleX={scale}
          scaleY={scale}
        >
          {/* Canvas shadow */}
          <Rect
            x={4}
            y={4}
            width={store.width}
            height={store.height}
            fill="rgba(0,0,0,0.1)"
            cornerRadius={0}
            listening={false}
            name="display-only"
          />

          {/* Page background */}
          <PageBackground store={store} />

          {/* Elements */}
          {page &&
            page.children.map((element) => (
              <ElementRenderer
                key={element.id}
                element={element}
                isSelected={store.selectedElementsIds.includes(element.id)}
                onTransformEnd={handleTransformEnd}
                onDragEnd={handleDragEnd}
              />
            ))}

          {/* Overlay that dims content outside the page */}
          <PageOverlayFrame store={store} />

          {/* Selection transformer */}
          <SelectionTransformer store={store} stageRef={stageRef} />
        </Layer>
      </Stage>

      {/* Page controls slot */}
      {PageControls !== undefined ? (
        PageControls === null ? null : <PageControls store={store} />
      ) : null}

      {/* Input fields popup */}
      <InputFieldsDialog store={store} enabled={showInputFieldsPopup} onComplete={onInputFieldsComplete} backTrigger={backTrigger} />
    </div>
  );
});

Workspace.displayName = 'Workspace';
export default Workspace;
export { Workspace };
